import { Check, ExternalLink, RefreshCw, UserPlus, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { api } from '@/api';
import { canApprove, IDENTITY_KINDS, isRequestKind, isReviewKind, type ReviewDecision, type WorkItem } from '@/api/areas/work';
import { Callout, Field, Section } from '@/components/blocks';
import { DataError } from '@/components/DataError';
import { KeyValue } from '@/components/KeyValue';
import { PageHeader, useDocumentTitle } from '@/components/PageHeader';
import { Loading, TechnicalDetail } from '@/components/States';
import { SystemReason } from '@/components/SystemReason';
import { useWrite, WriteError } from '@/components/Write';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/Badge';
import { formatDateTime } from '@/lib/format';
import { useApi } from '@/lib/useApi';
import { ActionDialog, FormActions, MfaHint, SelectField } from '../compliance/ui';
import { SeverityBadge, sourcePath, StatusBadge, useSourceName, useWorkSummary } from './shared';
import { DeclarationReviewPanel } from '../declarations/DeclarationReviewPanel';

type Dialog = 'approve' | 'reject' | 'resolve' | 'assign' | null;


export function DecisionForm({ item, dialog, onDone, onCancel }: { item: WorkItem; dialog: 'approve' | 'reject' | 'resolve'; onDone: () => void; onCancel: () => void }) {
  const { t } = useTranslation('work');
  const { t: tc } = useTranslation();
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const write = useWrite((body: { decision?: ReviewDecision; reason: string; customer_note?: string }, opts) => api.resolveWorkItem(item.id, body, opts));
  const decision: ReviewDecision | undefined = dialog === 'approve' ? 'APPROVE' : dialog === 'reject' ? 'REJECT' : undefined;
  return (
    <form
      className="grid gap-4"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!reason.trim()) return;
        const done = await write.run({ decision, reason: reason.trim(), customer_note: dialog === 'reject' && note.trim() ? note.trim() : undefined });
        if (done) onDone();
      }}
    >
      <Field label={t('form.reason')} hint={t('form.reasonHint')}>
        <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} disabled={write.busy} />
      </Field>
      {dialog === 'reject' && item.dossier_id ? (
        <Field label={t('reject.note')} hint={t('reject.noteHint')}>
          <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} disabled={write.busy} />
        </Field>
      ) : null}
      {decision ? <MfaHint permission="dossier.review" /> : null}
      {write.state.phase === 'error' ? <WriteError error={write.state.error} /> : null}
      <FormActions>
        <Button type="button" variant="outline" onClick={onCancel} disabled={write.busy}>
          {tc('action.cancel')}
        </Button>
        <Button type="submit" variant={dialog === 'reject' ? 'destructive' : 'default'} disabled={write.busy || !reason.trim()}>
          {t(`${dialog}.submit`)}
        </Button>
      </FormActions>
    </form>
  );
}

export function AssignForm({ item, onDone, onCancel }: { item: WorkItem; onDone: () => void; onCancel: () => void }) {
  const { t } = useTranslation('work');
  const { t: tc } = useTranslation();
  const staff = useApi((signal) => api.listWorkAssignees({ signal }), []);
  const [who, setWho] = useState(item.assigned_to ?? '');
  const write = useWrite((body: { assignee_id?: string }, opts) => api.assignWorkItem(item.id, body, opts));
  if (staff.status === 'loading') return <Loading rows={2} />;
  if (staff.status === 'error') return <DataError error={staff.error} onRetry={staff.reload} />;
  return (
    <form
      className="grid gap-4"
      onSubmit={async (e) => {
        e.preventDefault();
        if (who && (await write.run({ assignee_id: who }))) onDone();
      }}
    >
      <SelectField label={t('assign.label')} value={who} options={staff.data.items.map((s) => ({ value: s.id, label: `${s.display_name} · ${s.email}` }))} onChange={setWho} />
      {write.state.phase === 'error' ? <WriteError error={write.state.error} /> : null}
      <FormActions>
        <Button type="button" variant="outline" onClick={onCancel} disabled={write.busy}>
          {tc('action.cancel')}
        </Button>
        <Button type="submit" disabled={write.busy || !who}>
          {t('assign.submit')}
        </Button>
      </FormActions>
    </form>
  );
}

export function FieldResults({ item }: { item: WorkItem }) {
  const { t } = useTranslation('work');
  const comparison = item.detail.comparison;
  const face = item.detail.face;
  if (!comparison && !face?.band) return null;
  const tone = (r: string | null | undefined) => (r === 'MATCH' ? 'ok' : r === 'MISMATCH' ? 'crit' : 'warn');
  return (
    <Section title={t('item.comparison')} description={t('item.comparisonNote')}>
      <KeyValue
        columns={3}
        rows={[
          ...(['name', 'document_number', 'birth'] as const).map(
            (f) =>
              [
                t(`fieldName.${f}`),
                comparison?.[f] ? (
                  <Badge key={f} tone={tone(comparison[f])}>
                    {t(`field.${comparison[f]}`)}
                  </Badge>
                ) : (
                  '—'
                ),
              ] as const,
          ),
          face?.band ? ([t('item.faceBand'), <Badge key="band" tone={face.band === 'SAME_PERSON' ? 'ok' : face.band === 'MAY_BE_SAME' ? 'warn' : 'crit'}>{face.band}</Badge>] as const) : null,
          face?.similarity ? ([t('item.similarity'), `${face.similarity} / ${t('item.threshold')} ${face.threshold ?? '—'}`] as const) : null,
        ]}
      />
    </Section>
  );
}

/** `/compliance/work/:id`: one work item, its facts and the decision. */
export function WorkItemPage() {
  const { id = '' } = useParams();
  const { t } = useTranslation('work');
  const { t: tc } = useTranslation();
  const result = useApi((signal) => api.getWorkItem(id, { signal }), [id]);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [done, setDone] = useState(false);
  const takeMe = useWrite((_: null, opts) => api.assignWorkItem(id, {}, opts));
  const summarize = useWorkSummary();
  const sourceName = useSourceName();
  useDocumentTitle(result.status === 'success' ? `${t(`kind.${result.data.kind}`)} · ${t('item.title')}` : t('item.title'));
  if (result.status === 'loading') return <Loading rows={6} />;
  if (result.status === 'error') return <DataError error={result.error} onRetry={result.reload} title={t('item.loadError')} />;
  const w = result.data;
  const review = isReviewKind(w.kind);
  const open = w.status === 'OPEN';
  const src = sourcePath(w);
  const finding = summarize(w);
  const finish = () => {
    setDialog(null);
    setDone(true);
    result.reload();
  };
  const approveText = IDENTITY_KINDS.includes(w.kind) ? t('approve.description.identity') : w.kind === 'DOSSIER_MANUAL_REVIEW' ? t('approve.description.dossier') : t('approve.description.other');
  const actions: ReactNode = open ? (
    <>
      <Button size="sm" variant="outline" disabled={takeMe.busy} onClick={async () => (await takeMe.run(null)) && result.reload()}>
        <UserPlus /> {t('item.assignMe')}
      </Button>
      <Button size="sm" variant="outline" onClick={() => setDialog('assign')}>
        {t('item.assignOther')}
      </Button>
      {isRequestKind(w.kind) ? (
        src ? (
          <Button size="sm" asChild>
            <Link to={src}>
              <ExternalLink /> {t('item.openRequest')}
            </Link>
          </Button>
        ) : null
      ) : review ? (
        <>
          {canApprove(w) ? (
            <Button size="sm" onClick={() => setDialog('approve')}>
              <Check /> {t('item.approve')}
            </Button>
          ) : null}
          <Button size="sm" variant="destructive" onClick={() => setDialog('reject')}>
            <X /> {t('item.reject')}
          </Button>
        </>
      ) : (
        <Button size="sm" onClick={() => setDialog('resolve')}>
          <Check /> {t('item.resolve')}
        </Button>
      )}
    </>
  ) : null;
  return (
    <>
      <PageHeader
        title={t(`kind.${w.kind}`)}
        subtitle={w.unicode_name ?? t('queue.noDomain')}
        crumbs={[{ label: t('crumbs.compliance') }, { label: t('crumbs.queue'), to: '/compliance/work' }]}
        actions={
          <>
            {actions}
            <Button size="sm" variant="ghost" onClick={result.reload}>
              <RefreshCw /> {tc('action.refresh')}
            </Button>
          </>
        }
      />
      <div className="space-y-6">
        {done ? <Callout tone="ok">{t('form.done')}</Callout> : null}
        {open && isRequestKind(w.kind) ? <Callout tone="neutral">{t('item.decidedOnRequest')}</Callout> : null}
        {takeMe.state.phase === 'error' ? <WriteError error={takeMe.state.error} /> : null}
        <Section title={t('item.facts')}>
          <div className="space-y-4">
            <p className="text-sm">{finding.text ?? t(`kind.${w.kind}`)}</p>
            <TechnicalDetail detail={finding.detail} />
            <KeyValue
              columns={3}
              rows={[
                [t('item.kind'), t(`kind.${w.kind}`)],
                [t('item.severity'), <SeverityBadge key="s" severity={w.severity} />],
                [t('item.status'), <StatusBadge key="st" item={w} />],
                [t('item.opened'), formatDateTime(w.opened_at)],
                w.due_at ? [t('item.due'), formatDateTime(w.due_at)] : null,
                [t('item.openedBy'), w.opened_by],
                [
                  t('item.source'),
                  src ? (
                    <Link key="src" to={src} className="inline-flex items-center gap-1 text-primary hover:underline">
                      {sourceName(w.source_kind)} <ExternalLink className="size-3.5" aria-hidden="true" />
                    </Link>
                  ) : (
                    sourceName(w.source_kind)
                  ),
                ],
                w.domain_name
                  ? [
                      t('item.domain'),
                      <Link key="d" to={`/domains/${encodeURIComponent(w.domain_name)}`} className="font-medium text-primary hover:underline">
                        {w.unicode_name ?? w.domain_name}
                      </Link>,
                    ]
                  : null,
                w.dossier_status ? [t('item.dossier'), `${w.dossier_status}${w.dossier_version ? ` · v${w.dossier_version}` : ''}`] : null,
                w.subject_id
                  ? [
                      t('item.subject'),
                      <Link key="sub" to={`/subjects/${encodeURIComponent(w.subject_id)}`} className="text-primary hover:underline">
                        {t('item.openSubject')}
                      </Link>,
                    ]
                  : null,
                w.verification_status ? [t('item.verification'), `${w.verification_method ?? ''} · ${w.verification_status}`] : null,
                w.detail.reason_codes?.length ? [t('item.reasonCodes'), w.detail.reason_codes.join(', ')] : null,
                w.detail.changed?.length ? [t('item.changed'), w.detail.changed.join(', ')] : null,
                [t('item.assignee'), w.assigned_to_display_name ?? '—'],
                w.assigned_at ? [t('item.assignedAt'), formatDateTime(w.assigned_at)] : null,
                w.first_action_at ? [t('item.firstAction'), formatDateTime(w.first_action_at)] : null,
              ]}
            />
          </div>
        </Section>
        <FieldResults item={w} />
        {w.kind === 'DECLARATION_REVIEW' ? <DeclarationReviewPanel item={w} /> : null}
        {!open ? (
          <Section title={t('item.decision')}>
            <KeyValue
              columns={2}
              rows={[
                [t('item.status'), <StatusBadge key="st" item={w} />],
                [t('item.decidedBy'), w.resolved_by_display_name ?? t('item.system')],
                [t('item.decidedAt'), formatDateTime(w.resolved_at)],
                [t('item.reason'), w.resolution_reason ? <SystemReason key="r" reason={w.resolution_reason} /> : '—'],
                w.resolution_detail ? [t('item.effect'), <code key="e" className="text-xs break-all">{JSON.stringify(w.resolution_detail)}</code>] : null,
              ]}
            />
          </Section>
        ) : null}
        <Section title={t('item.raw')}>
          <pre className="max-h-80 overflow-auto rounded-md bg-muted p-3 text-xs">{JSON.stringify(w.detail, null, 2)}</pre>
        </Section>
      </div>
      <ActionDialog open={dialog === 'assign'} onOpenChange={(o) => setDialog(o ? 'assign' : null)} title={t('assign.title')}>
        <AssignForm item={w} onDone={finish} onCancel={() => setDialog(null)} />
      </ActionDialog>
      <ActionDialog open={dialog === 'approve'} onOpenChange={(o) => setDialog(o ? 'approve' : null)} title={t('approve.title')} description={approveText}>
        <DecisionForm item={w} dialog="approve" onDone={finish} onCancel={() => setDialog(null)} />
      </ActionDialog>
      <ActionDialog
        open={dialog === 'reject'}
        onOpenChange={(o) => setDialog(o ? 'reject' : null)}
        title={t('reject.title')}
        description={w.dossier_id ? t('reject.description') : t('reject.descriptionNoDossier')}
      >
        <DecisionForm item={w} dialog="reject" onDone={finish} onCancel={() => setDialog(null)} />
      </ActionDialog>
      <ActionDialog open={dialog === 'resolve'} onOpenChange={(o) => setDialog(o ? 'resolve' : null)} title={t('resolve.title')} description={t('resolve.description')}>
        <DecisionForm item={w} dialog="resolve" onDone={finish} onCancel={() => setDialog(null)} />
      </ActionDialog>
    </>
  );
}
